import { type KeyboardEvent, useEffect, useRef } from 'react';
import {
  Box,
  Column,
  Label,
  Popover,
  Row,
  ScrollArea,
  TextField,
  Typography,
} from '@tempmee/react';
import { LocationListItem } from '@tempmee/react-blocks';
import { OutlineSearchIcon } from '@tempmee/react-icons';
import { useLocationList } from '~/hooks/useLocationList';
import { useParentOffice } from '~/hooks/useParentOffice';
import { getOfficeAvatarUrl } from '~/utils/OfficeUtils';
import { toDataCySlug } from '~/utils/String';
import styles from './LocationMenu.module.scss';

/**
 * The same row shell `LocationListItem` draws, so the roll-up row reads as one of the list.
 * `@tempmee/react-blocks` has no admin roll-up variant of `LocationListItem` (Figma
 * `Atom/LocationMenuItems` drawn without an avatar, a chip over the organization name), so
 * this row and its keyboard handling are built here; swap both for the block once it ships.
 */
const ROLL_UP_ROW_CLASS =
  'w-full cursor-pointer border-b border-divider bg-background-paper transition-colors hover:bg-action-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary';

interface ILocationMenuProps {
  /** The trigger the dropdown opens under; `null` keeps it closed. */
  anchorEl: HTMLElement | null;
  /** An outside press, Escape, or a pick asks the trigger to close it. */
  onClose: () => void;
  /** Called with the picked office's id; the parent office's id for "All locations". */
  onSelect: (officeId: string) => void;
}

/**
 * The top-bar "Change location" dropdown (GOTU-8264, Figma 4713-12282): a search
 * box, an "All locations" row for the parent office, then one row per location.
 * Its body mounts only while open, so each opening starts with an empty search.
 */
export const LocationMenu = ({ anchorEl, onClose, onSelect }: ILocationMenuProps) => (
  <Popover
    open={anchorEl !== null}
    anchorEl={anchorEl}
    onClose={onClose}
    placement="bottom-end"
    offset={12}
    surface="menu"
    aria-label="Change location"
    className={`p-4 shadow-elevation-2 ${styles.menu}`}
  >
    <LocationMenuBody onSelect={onSelect} />
  </Popover>
);

/** The location name in the top-bar trigger, cut to the design's width. */
export const LocationMenuTriggerLabel = ({ name }: { name: string }) => (
  <Box as="span" className={styles.triggerLabel} title={name}>
    {name}
  </Box>
);

const LocationMenuBody = ({ onSelect }: Pick<ILocationMenuProps, 'onSelect'>) => {
  const { locations, masterOfficeId, parentLocation, query, setQuery } = useLocationList();
  const { office: signedInOffice } = useParentOffice();
  const searchInputRef = useRef<HTMLInputElement>(null);

  // The body mounts when the dropdown opens, so focusing on mount = focusing on open.
  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  // Until organizations exist (GOTU-7475), the parent office's name stands for the organization.
  const organizationName = parentLocation?.office_name ?? signedInOffice?.office_name ?? '';
  // The roll-up row stays pinned, as the way back to every location; the search filters the rest.
  const locationRows = locations.filter((office) => office.id !== masterOfficeId);

  const selectAllLocations = () => {
    if (masterOfficeId) onSelect(masterOfficeId);
  };

  const handleRollUpKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      selectAllLocations();
    }
  };

  return (
    <Column>
      <TextField
        ref={searchInputRef}
        label="Search"
        aria-label="Search locations"
        leadingIcon={<OutlineSearchIcon />}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        data-cy="search-office-name"
      />
      <ScrollArea className={styles.list} data-cy="office-dropdown-menu">
        {masterOfficeId && (
          <Row
            align="center"
            px={2}
            py={4}
            role="button"
            tabIndex={0}
            onClick={selectAllLocations}
            onKeyDown={handleRollUpKeyDown}
            className={ROLL_UP_ROW_CLASS}
            data-cy="office-option-all-locations"
          >
            <Column align="start" gap={1} className="min-w-0">
              <Label severity="brand" variant="standard">
                All locations
              </Label>
              <Typography variant="body1" numberOfLines={1}>
                {organizationName}
              </Typography>
            </Column>
          </Row>
        )}
        {locationRows.map((office) => (
          <LocationListItem
            key={office.id}
            locationId={office.id}
            name={office.office_name}
            address={office.address?.[0]?.formatted_address}
            avatarUrl={getOfficeAvatarUrl(office.profile_image)}
            onLocationClick={onSelect}
            testID={`location-row-${office.id}`}
            data-cy={`office-option-${toDataCySlug(office.office_name ?? '')}`}
          />
        ))}
        {locationRows.length === 0 && (
          <Box px={2} py={4}>
            <Typography variant="body1" data-cy="no-offices">
              No locations match
            </Typography>
          </Box>
        )}
      </ScrollArea>
    </Column>
  );
};
